import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for the browser app.
 *
 * Reads configuration from Vite env vars:
 *   - VITE_SENTRY_DSN       (required to enable Sentry; if absent Sentry stays inert)
 *   - VITE_SENTRY_ENVIRONMENT (optional, defaults to Vite mode)
 *   - VITE_SENTRY_TRACES_SAMPLE_RATE   (optional, 0..1)
 *   - VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE (optional, 0..1)
 *   - VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE (optional, 0..1)
 *
 * Must be called before the router is created so the React Router v7
 * data-router instrumentation can wrap it.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // No DSN -> Sentry disabled (e.g. local dev without a project). Keep quiet.
  if (!dsn) return;

  const num = (value: string | undefined, fallback: number) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance tracing
    tracesSampleRate: num(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
      import.meta.env.PROD ? 0.2 : 1.0
    ),
    // Session Replay
    replaysSessionSampleRate: num(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      import.meta.env.PROD ? 0.1 : 0
    ),
    replaysOnErrorSampleRate: num(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
      1.0
    ),
  });
}
